// The building blocks of the banks of the client application (SEP-293).
//
// The individual bank (`client-application-questions.ts`) came first, and
// each later bank takes from it each question that the paper forms share.
// This module holds the helpers that do that, and the helper that builds a
// repeated person group. A bank of a form other than the individual form
// imports this module. The individual bank does not, so its ids cannot
// change here.
//
// # A SHARED ID HAS ONE MEANING
//
// The stored answer is the option label, so a shared id keeps the kind and
// the option labels of the individual bank in every bank (rule H6 of the
// design). A bank can give the question its own label and its own explainer,
// because the paper form of a company says "the company" where the paper
// form of a person says "you".
//
// # A PERSON GROUP IS FLAT
//
// The engine has no repeated section. A group of persons is a flat list of
// questions, `<prefix>_<n>_<field>`, as the individual bank already does for
// the dependants and the rows of tax residency. The group after the first
// opens with a yes or no question, `has_<prefix>_<n>`, that shows only while
// the group before it is open. The visibility rule of the engine is
// transitive, so a client who closes group 2 also closes groups 3 to 5.

import {
    clientApplicationQuestion,
    clientApplicationSection,
    type ClientApplicationAddressTargets,
    type ClientApplicationCondition,
    type ClientApplicationQuestion,
    type ClientApplicationSection,
} from './client-application-questions';

/** The fields of a question that a version of the individual form governs. */
type VersionKeys = 'introducedIn' | 'retiredIn' | 'wordingIn' | 'showWhenIn';

/** What a bank can change when it takes a question of the individual bank. */
export type ClientApplicationQuestionChanges = Partial<
    Omit<ClientApplicationQuestion, 'id' | 'kind' | 'options' | VersionKeys>
> & {
    /** Remove the explainer of the individual bank. */
    readonly dropExplainer?: true;
    /** Remove the option notes of the individual bank. */
    readonly dropOptionNotes?: true;
    /** Remove the condition of the individual bank. */
    readonly dropShowWhen?: true;
    /** Remove the required flag of the individual bank, and opt out of a bank that requires every answer. */
    readonly optional?: true;
};

/**
 * One question of the individual bank, as one version holds it, for a bank of
 * another form.
 *
 * The question keeps its id, its kind and its options. It loses the version
 * fields of the individual line, because the other bank has its own line.
 * `changes` gives the words of the other bank. It throws when the version
 * does not hold the id, so a bank can never take a question that does not
 * exist.
 */
export function individualQuestion(
    id: string,
    version: '2.0' | '3.0',
    changes: ClientApplicationQuestionChanges = {},
): ClientApplicationQuestion {
    const source = clientApplicationQuestion(id, version);
    if (source === undefined)
        throw new Error(`version ${version} of the individual form has no "${id}"`);
    const {
        introducedIn: _introducedIn,
        retiredIn: _retiredIn,
        wordingIn: _wordingIn,
        showWhenIn: _showWhenIn,
        ...question
    } = source;
    const { dropExplainer, dropOptionNotes, dropShowWhen, optional, ...words } = changes;
    const out: { -readonly [K in keyof ClientApplicationQuestion]?: unknown } = {
        ...question,
        ...words,
    };
    if (dropExplainer === true && words.explainer === undefined) delete out.explainer;
    if (dropOptionNotes === true) delete out.optionNotes;
    if (dropShowWhen === true && words.showWhen === undefined) {
        delete out.showWhen;
        delete out.showWhenRule;
    }
    if (optional === true) {
        delete out.required;
        out.optional = true;
    }
    return out as ClientApplicationQuestion;
}

/**
 * The changes of a bank, by question id, from a list of pairs. A question id
 * is snake_case, and a list keeps it out of the key position of an object
 * literal, which the snake-case guard reads as a wire key.
 */
export function questionChanges(
    pairs: ReadonlyArray<readonly [string, ClientApplicationQuestionChanges]>,
): Readonly<Record<string, ClientApplicationQuestionChanges>> {
    return Object.fromEntries(pairs);
}

/** Each question of one section of the individual bank, in one version, for another bank. */
export function individualSectionQuestions(
    sectionId: string,
    version: '2.0' | '3.0',
    ids: readonly string[],
    changes: Readonly<Record<string, ClientApplicationQuestionChanges>> = {},
): ClientApplicationQuestion[] {
    const section = clientApplicationSection(sectionId, version);
    if (section === undefined)
        throw new Error(`version ${version} of the individual form has no section "${sectionId}"`);
    const held = new Set(section.questions.map((question) => question.id));
    return ids.map((id) => {
        if (!held.has(id))
            throw new Error(`section "${sectionId}" of version ${version} has no "${id}"`);
        return individualQuestion(id, version, changes[id]);
    });
}

/**
 * The first section that each form shares (SEP-293, section 1.4 of the
 * design): the service type, the account type and the Investment Manager.
 * The answers that select the form live here, so each bank holds this one
 * object and the answers stay when the form changes.
 */
export function sharedServiceTypeSection(): ClientApplicationSection {
    const section = clientApplicationSection('service_type', '3.0');
    if (section === undefined) throw new Error('the individual form has no service type section');
    return section;
}

// ---------------------------------------------------------------------------
// The person group
// ---------------------------------------------------------------------------

/** The address lookup targets of one address group, from its id prefix. */
export function addressTargets(prefix: string): ClientApplicationAddressTargets {
    return {
        houseNoName: `${prefix}_house_no_name`,
        street: `${prefix}_street`,
        townCity: `${prefix}_town_city`,
        county: `${prefix}_county`,
        postcode: `${prefix}_postcode`,
        country: `${prefix}_country`,
    };
}

/**
 * The six questions of one address, from its id prefix.
 *
 * The first question carries the address lookup. `required` marks the house,
 * the town, the postcode and the country, as the individual bank does.
 */
export function addressQuestions(input: {
    readonly prefix: string;
    readonly group: string;
    readonly required: boolean;
    readonly houseLabel?: string;
    readonly showWhen?: ClientApplicationQuestion['showWhen'];
}): ClientApplicationQuestion[] {
    const { prefix, group, required, showWhen } = input;
    const need = required ? ({ required: true } as const) : {};
    const when = showWhen === undefined ? {} : { showWhen };
    return [
        {
            id: `${prefix}_house_no_name`,
            kind: 'short_text',
            label: input.houseLabel ?? 'House no./name',
            ...need,
            ...when,
            group,
            addressLookup: addressTargets(prefix),
        },
        { id: `${prefix}_street`, kind: 'short_text', label: 'Street', ...when, group },
        {
            id: `${prefix}_town_city`,
            kind: 'short_text',
            label: 'Town/City',
            ...need,
            ...when,
            group,
        },
        { id: `${prefix}_county`, kind: 'short_text', label: 'County', ...when, group },
        {
            id: `${prefix}_postcode`,
            kind: 'short_text',
            label: 'Postcode',
            textFormat: 'postcode',
            ...need,
            ...when,
            group,
        },
        {
            id: `${prefix}_country`,
            kind: 'country_select',
            label: 'Country',
            ...need,
            ...when,
            group,
        },
    ];
}

/** The country code of the United Kingdom, which the country list stores. */
export const CLIENT_APPLICATION_UNITED_KINGDOM = 'GB';

/** How one form prints its person group. */
export interface ClientApplicationPersonGroupOptions {
    /** The id prefix of the group, for example `stakeholder`. */
    readonly prefix: string;
    /** The number of blocks that the paper form prints. */
    readonly count: number;
    /** The heading of one block, for example "Personal information (2)". */
    readonly heading: (n: number) => string;
    /** The label of the yes or no question that opens a block after the first. */
    readonly openerLabel: string;
    /**
     * The label of a yes or no question that opens block 1, when block 1 can
     * be empty. Absent: block 1 always shows.
     */
    readonly firstOpenerLabel?: string;
    /** The label of the capacity question. Absent: the block asks no capacity. */
    readonly capacityLabel?: string;
    /** `three`: mobile, home and business number. `one`: one telephone number. */
    readonly phones: 'three' | 'one';
    /** The explainer of the National Client Identifier. */
    readonly nationalClientIdentifierExplainer: string;
    /** The label of the National Client Identifier, when the form names another appendix. */
    readonly nationalClientIdentifierLabel?: string;
}

/** The id of the yes or no question that opens one block of a person group. */
export function personGroupOpenerId(prefix: string, n: number): string {
    return `has_${prefix}_${n}`;
}

/**
 * The questions of a repeated person group (SEP-293, section 1.6 of the
 * design).
 *
 * Each block holds the questions of one person, `<prefix>_<n>_<field>`, with
 * the field names of rule H4. Block 1 always shows, unless the form gives a
 * `firstOpenerLabel`. Each later block opens with `has_<prefix>_<n>`, which
 * shows only while the block before it is open.
 */
export function personGroup(
    options: ClientApplicationPersonGroupOptions,
): ClientApplicationQuestion[] {
    const questions: ClientApplicationQuestion[] = [];
    for (let n = 1; n <= options.count; n++) {
        questions.push(...personBlock(options, n));
    }
    return questions;
}

function personBlock(
    options: ClientApplicationPersonGroupOptions,
    n: number,
): ClientApplicationQuestion[] {
    const { prefix } = options;
    const p = `${prefix}_${n}`;
    const heading = options.heading(n);
    const sub = (name: string) => `${heading}: ${name}`;
    const hasOpener = n > 1 || options.firstOpenerLabel !== undefined;
    const openerId = personGroupOpenerId(prefix, n);
    const opens: ClientApplicationCondition | undefined = hasOpener
        ? { questionId: openerId, equals: ['Yes'] }
        : undefined;
    // The opener of block n shows while block n - 1 is open. The opener of
    // block 1 always shows.
    const previousOpener =
        n > 1 && (n > 2 || options.firstOpenerLabel !== undefined)
            ? { questionId: personGroupOpenerId(prefix, n - 1), equals: ['Yes'] }
            : undefined;

    const block: ClientApplicationQuestion[] = [];
    if (hasOpener) {
        block.push({
            id: openerId,
            kind: 'yes_no',
            label: n === 1 ? options.firstOpenerLabel! : options.openerLabel,
            required: true,
            group: heading,
            ...(previousOpener === undefined ? {} : { showWhen: previousOpener }),
        });
    }

    const fields: ClientApplicationQuestion[] = [];
    if (options.capacityLabel !== undefined) {
        fields.push({
            id: `${p}_capacity`,
            kind: 'short_text',
            label: options.capacityLabel,
            required: true,
            group: heading,
        });
    }
    fields.push(
        {
            id: `${p}_title`,
            kind: 'single_select',
            label: 'Title',
            required: true,
            options: ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Other'],
            group: heading,
        },
        {
            id: `${p}_title_other`,
            kind: 'short_text',
            label: 'Other (please specify)',
            showWhen: { questionId: `${p}_title`, equals: ['Other'] },
            group: heading,
        },
        {
            id: `${p}_surname`,
            kind: 'short_text',
            label: 'Surname',
            required: true,
            group: heading,
        },
        {
            id: `${p}_forenames`,
            kind: 'short_text',
            label: 'Forename(s)',
            required: true,
            group: heading,
        },
        {
            id: `${p}_date_of_birth`,
            kind: 'date',
            label: 'Date of Birth',
            required: true,
            group: heading,
        },
        {
            id: `${p}_country_of_birth`,
            kind: 'country_select',
            label: 'Country of Birth',
            required: true,
            group: heading,
        },
        {
            id: `${p}_town_of_birth`,
            kind: 'short_text',
            label: 'Town of birth',
            required: true,
            group: heading,
        },
        ...addressQuestions({
            prefix: `${p}_current_address`,
            group: sub('current residential address'),
            required: true,
        }),
        {
            id: `${p}_lived_at_current_address_3_years`,
            kind: 'yes_no',
            label: 'Have you lived at your current address for 3 years or more?',
            explainer:
                "If you've lived at your current address for less than 3 years, please provide your previous address below:",
            required: true,
            group: sub('previous residential address'),
        },
        ...addressQuestions({
            prefix: `${p}_previous_address`,
            group: sub('previous residential address'),
            required: true,
            showWhen: { questionId: `${p}_lived_at_current_address_3_years`, equals: ['No'] },
        }),
    );
    if (options.phones === 'three') {
        fields.push(
            {
                id: `${p}_mobile_number`,
                kind: 'short_text',
                label: 'Mobile number',
                textFormat: 'phone',
                group: sub('contact details'),
            },
            {
                id: `${p}_home_number`,
                kind: 'short_text',
                label: 'Home number',
                textFormat: 'phone',
                group: sub('contact details'),
            },
            {
                id: `${p}_business_number`,
                kind: 'short_text',
                label: 'Business number',
                textFormat: 'phone',
                group: sub('contact details'),
            },
        );
    } else {
        fields.push({
            id: `${p}_telephone_number`,
            kind: 'short_text',
            label: 'Telephone number',
            textFormat: 'phone',
            group: sub('contact details'),
        });
    }
    fields.push(
        {
            id: `${p}_email_address`,
            kind: 'short_text',
            label: 'Email address',
            textFormat: 'email',
            required: true,
            group: sub('contact details'),
        },
        {
            id: `${p}_country_of_nationality`,
            kind: 'country_select',
            label: 'Nationality',
            required: true,
            group: sub('nationality & tax status'),
        },
        {
            id: `${p}_second_country_of_nationality`,
            kind: 'country_select',
            label: 'Second nationality (if a dual national)',
            group: sub('nationality & tax status'),
        },
        {
            id: `${p}_national_client_identifier`,
            kind: 'short_text',
            label:
                options.nationalClientIdentifierLabel ??
                'National Client Identifier (see Appendix III. If UK national, use NI no.)',
            explainer: options.nationalClientIdentifierExplainer,
            required: true,
            textFormatWhen: {
                questionId: `${p}_country_of_nationality`,
                equals: [CLIENT_APPLICATION_UNITED_KINGDOM],
                format: 'national_insurance_number',
            },
            group: sub('nationality & tax status'),
        },
        ...taxResidencyRows(p, sub('nationality & tax status')),
        {
            id: `${p}_us_person`,
            kind: 'yes_no',
            label: 'Are you a US Person?',
            required: true,
            group: sub('nationality & tax status'),
        },
    );

    // A field with no condition of its own opens with the block. A field
    // with a condition opens with its parent, and the parent opens with the
    // block, so the transitive rule of the engine closes it with the block.
    for (const field of fields) {
        block.push(
            opens !== undefined && field.showWhen === undefined
                ? { ...field, showWhen: opens }
                : field,
        );
    }
    return block;
}

/** The three rows of tax residency of one person, rows 2 and 3 behind an opener. */
function taxResidencyRows(p: string, group: string): ClientApplicationQuestion[] {
    const rows: ClientApplicationQuestion[] = [];
    for (const r of [1, 2, 3]) {
        const opener = r === 1 ? undefined : `${p}_has_tax_residency_${r}`;
        if (opener !== undefined) {
            rows.push({
                id: opener,
                kind: 'yes_no',
                label:
                    r === 2
                        ? 'Tax resident or a citizen of another country?'
                        : 'Tax resident or a citizen of a third country?',
                group,
                ...(r === 3
                    ? { showWhen: { questionId: `${p}_has_tax_residency_2`, equals: ['Yes'] } }
                    : {}),
            });
        }
        const when =
            opener === undefined ? {} : { showWhen: { questionId: opener, equals: ['Yes'] } };
        rows.push(
            {
                id: `${p}_tax_residency_${r}_country`,
                kind: 'country_select',
                label: `Tax residency and citizenship — country ${r}`,
                ...(r === 1
                    ? {
                          required: true as const,
                          explainer:
                              'Tax residency and citizenship (if more than one, provide all countries)',
                      }
                    : {}),
                ...when,
                group,
            },
            {
                id: `${p}_tax_residency_${r}_tax_resident`,
                kind: 'yes_no',
                label: `Country ${r} — Tax Resident`,
                ...when,
                group,
            },
            {
                id: `${p}_tax_residency_${r}_citizen`,
                kind: 'yes_no',
                label: `Country ${r} — Citizen`,
                ...when,
                group,
            },
            {
                id: `${p}_tax_residency_${r}_tin`,
                kind: 'short_text',
                label: `Country ${r} — Tax Identification Number (if applicable)`,
                ...when,
                group,
            },
        );
    }
    return rows;
}

/** The id of the yes or no question that opens one row of a repeated table. */
export function rowOpenerId(prefix: string, n: number): string {
    return `has_${prefix}_${n}`;
}
